import {SlotsPlaceholder} from '@wix/widget-plugins-ooi';
import {ShowOnDesktopOnly} from '@wix/wixstores-client-common-components/dist/es/src/HOC/responsive/ShowOnDesktopOnly/ShowOnDesktopOnly';
import {ShowOnMobileOnly} from '@wix/wixstores-client-common-components/dist/es/src/HOC/responsive/ShowOnMobileOnly/ShowOnMobileOnly';
import {ImageMode} from '@wix/wixstores-client-core/dist/es/src/media/constants';
import {
  convertConfigToNumber,
  getMainImageRatio,
  isVideo,
} from '@wix/wixstores-client-core/dist/es/src/media/mediaService';
import cx from 'classnames';
import imagesLoaded from 'imagesloaded';
import _ from 'lodash';
import md5 from 'md5';
import React from 'react';
import stylesParams from '../../../../components/ProductPage/stylesParams';
import {
  GalleryNavigationLocation,
  GalleryNavigationPosition,
  GalleryNavigationType,
  ImageRatio,
  Layout,
  ProductPageSlotIds,
} from '../../../constants';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {SPECS} from '../../../specs';
import {DimensionsConfig, IProductDTO, LayoutConfig} from '../../../types/app-types';
import {ModalGalleryLayout} from '../../ModalGallery/ModalGalleryLayout';
import {Mode, WishlistButton} from '../../ProductPageButtonsContainer/WishlistButton/WishlistButton';
import {MainMedia} from '../MainMedia/MainMedia';
import {ProductImageDataHook} from '../MainMedia/ProductImage/ProductImage';
import {ProductGalleryNavigation} from '../ProductGalleryNavigation/ProductGalleryNavigation';
import {ProductGalleryContext, ProductGalleryProvider} from '../ProductGalleryProvider/ProductGalleryProvider';
import {getImageRatio} from './ProductGalleryLayout.cssVars';
import style from './ProductGalleryLayout.scss';
import {MediaNavigationArrows} from '../MediaNavigationArrows/MediaNavigationArrows';
import {ProductMediaDots} from './ProductMediaDots/ProductMediaDots';
import {ProductMediaSwiper} from './ProductMediaSwiper/ProductMediaSwiper';
import {ThumbnailDataHooks} from './ProductMediaSwiper/Thumbnail/Thumbnail';
import {ProductRibbonsOnImage} from '../../ProductRibbons/ProductRibbons';

export interface ProductGalleryLayoutProps extends ProvidedGlobalProps {
  layoutConfig: LayoutConfig;
  imageMode: ImageMode;
  imageRatioId: number;
  navigationType: GalleryNavigationType;
  navigationPosition: GalleryNavigationPosition;
  navigationLocation: GalleryNavigationLocation;
  layout: Layout;
}

export const enum ProductGalleryLayoutDataHook {
  Root = 'product-gallery-root',
  MainMediaContainer = 'main-media-container',
  MediaNavigation = 'product-media-navigation-root',
}

class ProductGalleryLayoutComponent extends React.Component<ProductGalleryLayoutProps> {
  private imageDimensionFallback = {height: 1, width: 1};

  private isThumbnailsNavigationOfPositions(positions): boolean {
    const {
      navigationType,
      navigationPosition,
      globals: {product},
    } = this.props;

    return (
      product.media.length > 1 &&
      navigationType === GalleryNavigationType.THUMBNAILS &&
      _.includes(positions, navigationPosition)
    );
  }

  private isHorizontalThumbnailsNavigation(): boolean {
    return this.isThumbnailsNavigationOfPositions([GalleryNavigationPosition.BOTTOM]);
  }

  private isVerticalThumbnailsNavigation(): boolean {
    return this.isThumbnailsNavigationOfPositions([GalleryNavigationPosition.LEFT, GalleryNavigationPosition.RIGHT]);
  }

  private getProductMediaNavigation(): JSX.Element {
    const {
      layoutConfig: {align, dimensions},
      navigationPosition,
      navigationLocation,
      navigationType,
      globals: {isMobile, product, experiments, useExperiments},
    } = this.props;

    const isNavigationPerBreakpointEnabled = useExperiments.enabled(SPECS.PPNavigationSectionPerBreakpoint);
    if (isNavigationPerBreakpointEnabled) {
      return <ProductGalleryNavigation align={align} />;
    }

    const withDots = navigationType === GalleryNavigationType.DOTS;
    const isNewNavigationDots = experiments.productMediaNavigationDots;
    const isNewThumbnailsDesignEnabled = experiments.thumbnailsDesignViewer;
    const conditionalClasses = {
      [style[`thumbnails-inside-${navigationPosition}-navigation`]]:
        !isMobile && navigationLocation === GalleryNavigationLocation.INSIDE,
      [style.insidePhoto]: isNewNavigationDots && navigationType === GalleryNavigationType.DOTS,
    };

    const layoutDimensions = {...dimensions.thumbnails};
    if (navigationPosition === GalleryNavigationPosition.BOTTOM || isMobile) {
      layoutDimensions.heightConf = {num: 50, unit: 'px'};
    } else {
      layoutDimensions.heightConf = {...dimensions.mainMedia.heightConf};
      layoutDimensions.widthConf = {num: 50, unit: 'px'};
    }

    return (
      <div
        data-hook={ProductGalleryLayoutDataHook.MediaNavigation}
        className={cx(
          {
            [style.navigationDots]: withDots,
            [style.thumbnails]: !withDots,
            [style[`old-thumbnails-position-${navigationPosition}`]]: !withDots && !isNewThumbnailsDesignEnabled,
            [style[`thumbnails-position-${navigationPosition}`]]: !withDots && isNewThumbnailsDesignEnabled,
          },
          ['slick-thumbnails-container-hook', conditionalClasses]
        )}>
        {withDots ? (
          <ProductMediaDots media={product.media} />
        ) : (
          <ProductMediaSwiper media={product.media} isVertical={this.isVerticalThumbnailsNavigation()} align={align} />
        )}
      </div>
    );
  }

  private getMainMediaContainerClass(): string {
    const {
      layoutConfig: {withMediaBorder, withImageRatio},
      navigationPosition,
      globals: {useExperiments},
    } = this.props;
    const withVerticalThumbnailsNavigation = this.isVerticalThumbnailsNavigation();
    const isNewThumbnailsDesignEnabled = useExperiments.enabled(SPECS.ThumbnailsDesignViewer);
    const newMediaDesignsEnabled = useExperiments.enabled(SPECS.ImageSettingsInViewer);
    const isVerticalThumbnailsFixEnabled = useExperiments.enabled(SPECS.FixVerticalThumbnailsPosition);
    return cx([
      style.mainMediaContainerOld,
      {
        [style[`main-media-${navigationPosition}-navigation`]]:
          withVerticalThumbnailsNavigation && isNewThumbnailsDesignEnabled,
        [style[`old-main-media-${navigationPosition}-navigation`]]:
          withVerticalThumbnailsNavigation && !isNewThumbnailsDesignEnabled,
        [style.mainMediaBorder]: withMediaBorder && !newMediaDesignsEnabled,
        [style.hasImageRatio]: withImageRatio,
        [style.contentBox]: !newMediaDesignsEnabled,
        [style.verticalThumbnailsAbsolutePosition]: withVerticalThumbnailsNavigation && isVerticalThumbnailsFixEnabled,
      },
    ]);
  }

  private getRootClasses(withImageRatio: boolean): string {
    const {
      layoutConfig: {marginBottom},
      globals: {useExperiments},
    } = this.props;
    const isNewThumbnailsDesignEnabled = useExperiments.enabled(SPECS.ThumbnailsDesignViewer);
    const horizontalNavigation = this.isHorizontalThumbnailsNavigation()
      ? [
          style[marginBottom],
          isNewThumbnailsDesignEnabled ? style.horizontalNavgationBottom : style.oldHorizontalNavgationBottom,
        ]
      : [];
    return cx([
      style.root,
      ...horizontalNavigation,
      {
        [style.rootAdaptive]: !withImageRatio,
      },
    ]);
  }

  private getAutoThumbnailCssVars(): React.CSSProperties | undefined {
    const {
      globals: {product, styles},
    } = this.props;

    const thumbnailImageRatio = styles.get(stylesParams.productPage_thumbnailImageRatio);
    const thumbnailsContainerSpacing = 15;
    const thumbnailsSize = styles.get(stylesParams.productPage_thumbnailsSize);

    if (thumbnailImageRatio !== ImageRatio.AUTO) {
      const actualRatio = getImageRatio(thumbnailImageRatio);
      const thumbnailsHeight = thumbnailsSize / actualRatio;
      const thumbnailArea = thumbnailsSize + thumbnailsContainerSpacing;
      return {
        '--productPage_thumbnailsHeight': `${thumbnailsHeight}px`,
        '--productPage_thumbnailArea': `${thumbnailArea}px`,
      } as React.CSSProperties;
    }

    const firstImage = product.media[0];
    if (!firstImage || !firstImage.width || !firstImage.height) {
      return undefined;
    }
    const ratio = firstImage.width / firstImage.height;
    const thumbnailsHeight = thumbnailsSize / ratio;
    const thumbnailArea = thumbnailsSize + thumbnailsContainerSpacing;

    return {
      '--productPage_thumbnailsHeight': `${thumbnailsHeight}px`,
      '--productPage_thumbnailArea': `${thumbnailArea}px`,
    } as React.CSSProperties;
  }

  public componentDidMount(): void {
    const {product, useExperiments} = this.props.globals;
    const isNavigationPerBreakpointEnabled = useExperiments.enabled(SPECS.PPNavigationSectionPerBreakpoint);

    if (!isNavigationPerBreakpointEnabled) {
      imagesLoaded(
        document.querySelectorAll(
          `[data-hook="${ThumbnailDataHooks.Image}"],[data-hook="${ThumbnailDataHooks.VideoOverlay}"],[data-hook="${ProductImageDataHook.ProductImage}"]`
        ),
        () => {
          this.props.globals.updateLayout && this.props.globals.updateLayout();
        }
      );
    }

    /* istanbul ignore next: todo: test */
    (product.media[0]?.height <= 1 || product.media[0]?.width <= 1) &&
      this.getImageDimensionFromUrl(product.media[0].fullUrl);
  }

  private get signature() {
    return md5(this.props.globals.product.media.reduce((acc, m) => acc.concat(m.url), ''));
  }

  /* istanbul ignore next: todo: test */
  private getImageDimensionFromUrl(url): void {
    const img = new Image();
    img.onload = () => {
      this.imageDimensionFallback = {height: img.naturalHeight, width: img.naturalWidth};
    };
    img.src = url;
  }

  private computeVerticalThumbnailLayout(
    withImageRatio: boolean,
    layoutDimensions: DimensionsConfig,
    keyMedia: IProductDTO['media'][0],
    imageRatioId: number
  ): {cssVars: React.CSSProperties; paddingTopOverride: string; isResponsive: boolean} | undefined {
    if (
      this.props.globals.isMobile ||
      !withImageRatio ||
      !this.props.globals.experiments.thumbnailsDesignViewer ||
      !this.isVerticalThumbnailsNavigation()
    ) {
      return undefined;
    }
    const useActualDimensions = imageRatioId === -1 && keyMedia && !isVideo(keyMedia);
    const {width, height} = useActualDimensions
      ? {width: keyMedia.width, height: keyMedia.height}
      : getMainImageRatio(keyMedia, imageRatioId).ratio;
    const thumbnailArea = this.props.globals.styles.get(stylesParams.productPage_thumbnailsSize) + 15;
    const fullWidth = convertConfigToNumber(layoutDimensions.widthConf, this.props.globals.dimensions.width);
    const effectiveWidth = fullWidth - thumbnailArea;

    const ratioCssVars = {
      '--productPage_mainMediaWidthRatio': String(width),
      '--productPage_mainMediaHeightRatio': String(height),
    } as React.CSSProperties;

    if (effectiveWidth > 0) {
      const heightPx = Math.ceil(effectiveWidth * (height / width));
      return {
        cssVars: {
          ...ratioCssVars,
          '--productPage_mainMediaHeight': `${heightPx}px`,
          height: `${heightPx}px`,
        } as React.CSSProperties,
        paddingTopOverride: `${heightPx}px`,
        isResponsive: false,
      };
    }

    return {
      cssVars: ratioCssVars,
      paddingTopOverride: `calc((100% - var(--productPage_thumbnailArea)) * var(--productPage_mainMediaHeightRatio) / var(--productPage_mainMediaWidthRatio))`,
      isResponsive: true,
    };
  }

  private getRootStyles(verticalThumbnailLayout: any): React.CSSProperties {
    const {useExperiments} = this.props.globals;
    const {mainMedia} = this.props.layoutConfig.dimensions;
    const {media} = this.props.globals.product;

    const nativeMediaAspectRatio = media[0]?.width && media[0]?.height ? media[0].width / media[0].height : 'initial';

    const layoutImageDimensionVars = {
      '--fixedMediaWidth': `${mainMedia.widthConf.num}${mainMedia.widthConf.unit}`,
      '--fixedMediaHeight': `${mainMedia.heightConf.num}${mainMedia.heightConf.unit}`,
      '--nativeMediaAspectRatio': nativeMediaAspectRatio,
    } as React.CSSProperties;

    const isNavigationPerBreakpointEnabled = useExperiments.enabled(SPECS.PPNavigationSectionPerBreakpoint);
    if (isNavigationPerBreakpointEnabled) {
      return layoutImageDimensionVars;
    }

    const isNewThumbnailsDesignEnabled = useExperiments.enabled(SPECS.ThumbnailsDesignViewer);
    if (!isNavigationPerBreakpointEnabled && isNewThumbnailsDesignEnabled) {
      return {...this.getAutoThumbnailCssVars(), ...verticalThumbnailLayout?.cssVars};
    }

    return undefined;
  }

  private buildMainMediaContainerClassName(
    isModalZoomEnabled: boolean,
    isNavigationPerBreakpointEnabled: boolean,
    withImageRatio: boolean,
    isResponsiveLayout: boolean | undefined,
    isMainMediaNavArrowsEnabled: boolean,
    ribbonsInProductPageEnabled: boolean
  ): string {
    return cx({
      'modal-zoom-icon': isModalZoomEnabled,
      [style.relativePositionedMediaWrapper]: isNavigationPerBreakpointEnabled && ribbonsInProductPageEnabled,
      [style.dynamicMediaDimensions]: isNavigationPerBreakpointEnabled && withImageRatio,
      [style.fixedMediaDimensions]: isNavigationPerBreakpointEnabled && !withImageRatio,
      [this.getMainMediaContainerClass()]: !isNavigationPerBreakpointEnabled,
      [style['main-media-responsive-aspect-ratio']]: !isNavigationPerBreakpointEnabled && isResponsiveLayout,
      [style.isMainMediaNavArrowsEnabled]: isMainMediaNavArrowsEnabled,
    });
  }

  /* istanbul ignore next: todo: test */
  private applyDimensionFallback(media: IProductDTO['media'][0] | undefined): void {
    if (media) {
      media.height = media.height > 0 ? media.height : this.imageDimensionFallback.height;
      media.width = media.width > 0 ? media.width : this.imageDimensionFallback.width;
    }
  }

  public render(): JSX.Element {
    const {
      layoutConfig: {withImageRatio, swipeToScroll, dimensions, withDynamicHeight, withMediaBorder},
      imageRatioId,
      imageMode,
      globals: {
        shouldShowWishlistButton,
        product,
        useExperiments,
        experiments: {
          renderProductPageSlots,
          mainMediaWrapperAsAnchorElement,
          mediaSlotOnBottomForResponsiveGallery,
          imageSettingsInViewer,
        },
        slots,
      },
      layout,
    } = this.props;

    const keyMedia = product.media[0];
    const layoutDimensions = dimensions.mainMedia;
    const withProductMediaNavigation = product.media.length > 1;
    const isPluginInstalledInMediaSlot = !!slots?.[ProductPageSlotIds.ProductPageMedia1];
    const isNavigationPerBreakpointEnabled = useExperiments.enabled(SPECS.PPNavigationSectionPerBreakpoint);
    const isMainMediaNavArrowsEnabled = useExperiments.enabled(SPECS.ProductPageMainMediaNavigationArrows);
    const ribbonsInProductPageEnabled = useExperiments.enabled(SPECS.ShowMultiRibbonsInProductPage);
    this.applyDimensionFallback(keyMedia);

    const verticalThumbnailLayout = this.computeVerticalThumbnailLayout(
      withImageRatio,
      layoutDimensions,
      keyMedia,
      imageRatioId
    );

    if (withImageRatio) {
      const {
        ratio: {width, height},
      } = getMainImageRatio(keyMedia, imageRatioId);
      layoutDimensions.heightConf = {
        num: Math.ceil(layoutDimensions.widthConf.num * (height / width)),
        unit: 'px',
      };
    }

    const borderWidth = 1;
    const shouldAdjustForBorder = withMediaBorder && !imageSettingsInViewer;
    if (shouldAdjustForBorder) {
      layoutDimensions.heightConf.num = layoutDimensions.heightConf.num - borderWidth * 2;
      layoutDimensions.widthConf.num = layoutDimensions.widthConf.num - borderWidth * 2;
    }
    /* istanbul ignore next */
    const MainMediaWrapperElement = mainMediaWrapperAsAnchorElement ? `button` : 'div';

    return (
      <div
        data-hook={ProductGalleryLayoutDataHook.Root}
        style={this.getRootStyles(verticalThumbnailLayout)}
        className={cx({
          [style.root]: isNavigationPerBreakpointEnabled,
          [style.navigationPerBreakpoint]: isNavigationPerBreakpointEnabled,
          [this.getRootClasses(withImageRatio)]: !isNavigationPerBreakpointEnabled,
        })}>
        <ProductGalleryProvider
          withImageRatio={withImageRatio}
          imageRatioId={imageRatioId}
          media={product.media}
          key={this.signature}
          paddingTopOverride={verticalThumbnailLayout?.paddingTopOverride}>
          <ProductGalleryContext.Consumer>
            {({selectedIndex}) => {
              return (
                <ModalGalleryLayout externalSelectedIndex={selectedIndex}>
                  {(modalZoom) => {
                    const mainMediaContainerClassName = this.buildMainMediaContainerClassName(
                      modalZoom.isEnabled,
                      isNavigationPerBreakpointEnabled,
                      withImageRatio,
                      verticalThumbnailLayout?.isResponsive,
                      isMainMediaNavArrowsEnabled,
                      ribbonsInProductPageEnabled
                    );
                    const shouldRenderMainMediaNavigationArrows =
                      isMainMediaNavArrowsEnabled && product.media.length > 1;
                    const mainMedia = (
                      <MainMedia
                        imageMode={imageMode}
                        swipeToScroll={swipeToScroll}
                        product={product}
                        layoutDimensions={layoutDimensions}
                        withDynamicHeight={withDynamicHeight}
                        isZoomModalEnabled={modalZoom.isEnabled}
                      />
                    );

                    if (mainMediaWrapperAsAnchorElement && shouldRenderMainMediaNavigationArrows) {
                      return (
                        <div
                          data-hook={ProductGalleryLayoutDataHook.MainMediaContainer}
                          onClick={modalZoom.open}
                          className={mainMediaContainerClassName}>
                          <button type="button" tabIndex={-1} className={style.mainMediaInteractiveButton}>
                            {mainMedia}
                          </button>
                          <MediaNavigationArrows />
                          <ProductRibbonsOnImage />
                        </div>
                      );
                    }

                    return (
                      <MainMediaWrapperElement
                        data-hook={ProductGalleryLayoutDataHook.MainMediaContainer}
                        {...(mainMediaWrapperAsAnchorElement && {type: 'button'})}
                        tabIndex={-1}
                        className={mainMediaContainerClassName}
                        onClick={modalZoom.open}>
                        {mainMedia}
                        {shouldRenderMainMediaNavigationArrows && <MediaNavigationArrows />}
                        <ProductRibbonsOnImage />
                      </MainMediaWrapperElement>
                    );
                  }}
                </ModalGalleryLayout>
              );
            }}
          </ProductGalleryContext.Consumer>
          {withProductMediaNavigation && this.getProductMediaNavigation()}
        </ProductGalleryProvider>
        {shouldShowWishlistButton && !renderProductPageSlots && (
          <ShowOnMobileOnly>
            <WishlistButton mode={Mode.FLOATING} />
          </ShowOnMobileOnly>
        )}
        {renderProductPageSlots && layout !== Layout.Stunning && (
          <>
            <ShowOnDesktopOnly>
              <div className={cx({[style.mediaSlotsContainer]: isPluginInstalledInMediaSlot})}>
                <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageMedia1} />
              </div>
            </ShowOnDesktopOnly>
            <ShowOnMobileOnly>
              <div
                className={cx({
                  [style.mediaSlotBottom]: mediaSlotOnBottomForResponsiveGallery,
                  [style.mediaSlotsContainer]: !mediaSlotOnBottomForResponsiveGallery && isPluginInstalledInMediaSlot,
                })}>
                <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageMedia1} />
              </div>
            </ShowOnMobileOnly>
          </>
        )}
      </div>
    );
  }
}

export const ProductGalleryLayout = withGlobalProps(ProductGalleryLayoutComponent);
